/**
 * Phrase plan, adaptation of the analyzer's phrases into `Phrase[]`.
 *
 * **Phrase detection does not live here.** It lives in analyzer-go,
 * which scores every measure boundary (IOI gaps, cadences, silences,
 * held notes, section boundaries) and publishes the result under
 * `analysis.phrases`. This module consumes that output verbatim: it
 * never re-cuts, merges, snaps or drops a boundary. A 15-measure
 * phrase stays a 15-measure phrase — if that is wrong, the fix belongs
 * upstream, not here. Keeping a second opinion on the front is exactly
 * the duplicated responsibility this module used to carry.
 *
 * What is left on this side:
 *   1. Map `AnalysisPhrase` → `Phrase` (identity, boundaries).
 *   2. Attach each phrase to a section: analyzer phrases carry none,
 *      and `Phrase.sectionId` feeds the phrase id and downstream views.
 *   3. Collect the phrase's notes per hand from the song parts.
 *   4. Degenerate fallbacks, exception paths only:
 *      - no usable analyzer phrase → 1 phrase per `AnalysisSection`;
 *      - no usable section either → regular split of the whole song;
 *      - song of ≤ 1 bar → 1 phrase covering everything.
 *   5. The same notes and grips read over the whole song, uncut, for a
 *      screen that does not play unit by unit (`buildSongSpan`).
 *
 * The output is deterministic: same input → same `Phrase[]`. That
 * stability is what `hashPhrasePlan` relies on to invalidate persisted
 * progression when this mapping changes (cf. spec §"Persistance").
 *
 * ── SongLike ──────────────────────────────────────────────────────
 *
 * We deliberately do **not** import `Song` from `@/lib/music/song`:
 * it transitively pulls `@/lib/track/instrument` (via `Part`), which
 * is forbidden inside `lib/learning-journey/`. Instead we declare
 * here the narrow structural contract this module needs. The real
 * `Song` already implements it shape-wise, so callers pass it as-is
 * without an adapter; tests mock just these methods.
 */

import type {
  AnalysisHandGrips,
  AnalysisPhrase,
  AnalysisPhraseSeam,
  AnalysisPhrases,
  AnalysisSection,
} from '@/lib/music/analyzer-types';
import type {
  Phrase,
  PhraseId,
  PhraseNote,
  PhraseGrip,
  SongSpan,
} from './types';
import {
  buildWorkUnits,
  toCutCandidate,
  type CutCandidate,
} from './work-units';

// ── Structural contract ───────────────────────────────────────────

/**
 * Minimal hand-annotated note shape consumed from `Part.getAnnotatedNotes()`.
 *
 * Mirrors the loaded fields used by `collectNotesByHand` in the
 * existing `slice-computation.ts`: tick, durationTicks, note (for the
 * MIDI value), and hand. We do not require the full `AnnotatedNote`
 * type so test mocks stay small.
 */
export type AnnotatedNoteLike = {
  readonly tick: number;
  readonly durationTicks: number;
  readonly note: { readonly midi: number };
  readonly hand: 'left' | 'right' | null;
  /** L'orthographe que l'analyseur donne à cette note. Voir `AnnotatedNote`. */
  readonly spelling?: string | null;
};

/** Minimal `Part` surface needed by `buildPhrasesFromSong`. */
export type PartLike = {
  getAnnotatedNotes(): readonly AnnotatedNoteLike[];
};

/**
 * Minimal `Song` surface needed by `buildPhrasesFromSong`.
 *
 * `getBars()` only feeds the degenerate fallbacks (bar count, and the
 * regular grid when we have nothing else); on the nominal path the
 * boundaries come ready-made from `getPhrases()`. We expect the
 * standard convention: 0-based bar indices, `[startTick, endTick)`.
 *
 * Measure numbers exposed in `Phrase` are **1-based** (`barIndex + 1`),
 * matching `AnalysisPhrase`/`AnalysisSection.startMeasure`/`endMeasure`.
 */
export type SongLike = {
  getDurationTicks(): number;
  getPhrases(): AnalysisPhrases | null;
  /**
   * Piece-long occupancy segmentation, or `null` when the analysis ran
   * without fingering or predates the field. Null is not a reason to compute
   * one here: the grip step simply does not run.
   */
  getHandGrips(): AnalysisHandGrips | null;
  /**
   * Ticks at which a chord starts, ascending. Only used to tell a bar line
   * that carries a harmonic change from one that does not — the analyzer
   * declined to duplicate this onto the seams, and reading two published
   * facts together is not re-deriving a decision.
   */
  getChordStartTicks(): readonly number[];
  getSections(): readonly AnalysisSection[];
  getParts(): readonly PartLike[];
  getBars(): readonly { startTick: number; endTick: number }[];
};

/**
 * Tick + measure interval shared by `AnalysisPhrase` and
 * `AnalysisSection`, the only fields we validate before turning either
 * one into a `Phrase`.
 */
type MeasureSpan = {
  readonly startTick: number;
  readonly endTick: number;
  readonly startMeasure: number;
  readonly endMeasure: number;
};

// ── Constants ─────────────────────────────────────────────────────

/**
 * Chunk size of the no-sections fallback, in measures.
 *
 * Not a phrase-length policy: phrase length is the analyzer's call.
 * This is only the arbitrary grid used when we have neither phrases
 * nor sections to lean on, so the journey still has *something* to
 * progress through.
 */
const FALLBACK_MEASURES_PER_PHRASE = 4;

/** Sentinel sectionId used when no section covers a phrase. */
const FALLBACK_SECTION_ID = 'fallback';

// ── Public API ────────────────────────────────────────────────────

/**
 * Build the deterministic phrase plan for a song.
 *
 * Branches, in order: analyzer phrases (nominal), then the fallbacks.
 * Always returns at least one phrase as long as the song has at least
 * one bar. Empty / zero-tick songs return an empty plan.
 */
export function buildPhrasesFromSong(song: SongLike): Phrase[] {
  const bars = song.getBars();

  if (bars.length === 0) {
    return [];
  }

  // Degenerate: ≤ 1 bar. One phrase covering everything.
  if (bars.length <= 1) {
    return buildUnits({
      sectionId: FALLBACK_SECTION_ID,
      indexInSection: 0,
      startTick: bars[0].startTick,
      endTick: bars[0].endTick,
      startMeasure: 1,
      endMeasure: 1,
      song,
    });
  }

  const sections = song.getSections().filter(isUsableSpan);
  const analysisPhrases = (song.getPhrases()?.items ?? []).filter(isUsableSpan);

  if (analysisPhrases.length > 0) {
    return mapAnalysisPhrases(analysisPhrases, sections, song);
  }

  if (sections.length === 0) {
    return buildSongGridPhrases(song, bars);
  }

  return buildSectionPhrases(sections, song);
}

/**
 * The whole song as one span: every note and every grip, uncut.
 *
 * For a screen that plays the piece through in one go and keeps the plan
 * only as a measuring grain — the harmonic journey. It used to put its units
 * end to end, and a unit only holds the notes that *start* in it: a chord
 * held across a unit boundary came out as two windows, the second of which
 * received no note and was dropped as empty. The frieze drew the chord up to
 * the boundary, then nothing until the next one — two beats of silence
 * in the middle of *The Sound of Silence*'s opening D minor.
 *
 * Read straight from the song rather than stitched from the plan, so that
 * nothing here depends on where the units fall. Same bounds as the plan's
 * fallbacks (the bars), and an empty song gives `null`, as it gives an empty
 * plan.
 */
export function buildSongSpan(song: SongLike): SongSpan | null {
  const bars = song.getBars();
  const first = bars[0];
  const last = bars[bars.length - 1];

  if (first === undefined || last === undefined) {
    return null;
  }

  const { startTick } = first;
  const { endTick } = last;
  const { left, right } = collectNotesByHand(song, startTick, endTick);

  return {
    startTick,
    endTick,
    notesByHand: { left, right },
    gripsByHand: {
      left: clipGrips(song, 'left', startTick, endTick, 1, bars.length, left),
      right: clipGrips(song, 'right', startTick, endTick, 1, bars.length, right),
    },
  };
}

/**
 * Deterministic short hash of a phrase plan.
 *
 * Uses FNV-1a 32-bit on a canonical string representation built from
 * the identifying fields of each phrase (id, ticks, midis). Returns a
 * fixed-width 8-char lowercase hex string.
 *
 * Goal: invalidate persisted progression when `buildPhrasesFromSong`
 * changes shape (added/moved/renamed phrases, changed boundaries, …).
 * A different output string ⇒ different hash ⇒ stale save discarded.
 *
 * Why FNV-1a: single-pass, tiny, well-behaved on short ASCII strings,
 * no dependency. Not crypto-grade, we only need collision resistance
 * across same-codebase variants of the algorithm, which FNV-1a covers.
 */
export function hashPhrasePlan(phrases: readonly Phrase[]): string {
  const canonical = phrases
    .map((p) => {
      const leftMidis = p.notesByHand.left.map((n) => n.midi).join(',');
      const rightMidis = p.notesByHand.right.map((n) => n.midi).join(',');

      return `${p.id}|${p.startTick}-${p.endTick}|L:${leftMidis}|R:${rightMidis}`;
    })
    .join('||');

  return fnv1a32Hex(canonical);
}

// ── Analyzer phrases (nominal path) ───────────────────────────────

/**
 * Rejects a span the journey could not run: non-finite bounds, empty or
 * inverted interval. We filter rather than repair, repairing would be
 * re-deciding a boundary, which is precisely what left this module.
 */
function isUsableSpan(span: MeasureSpan): boolean {
  return (
    Number.isFinite(span.startTick) &&
    Number.isFinite(span.endTick) &&
    span.endTick > span.startTick &&
    Number.isFinite(span.startMeasure) &&
    Number.isFinite(span.endMeasure) &&
    span.endMeasure >= span.startMeasure
  );
}

/**
 * Map the analyzer's phrases 1:1, in the order it emitted them.
 *
 * That order *is* the journey order, so we do not sort: reordering
 * would silently disagree with the upstream decision.
 */
function mapAnalysisPhrases(
  items: readonly AnalysisPhrase[],
  sections: readonly AnalysisSection[],
  song: SongLike,
): Phrase[] {
  const nextIndexBySection = new Map<string, number>();
  const phrases: Phrase[] = [];

  for (const item of items) {
    const sectionId = resolveSectionId(item, sections);
    const indexInSection = nextIndexBySection.get(sectionId) ?? 0;

    const units = buildUnits({
      sectionId,
      indexInSection,
      startTick: item.startTick,
      endTick: item.endTick,
      startMeasure: item.startMeasure,
      endMeasure: item.endMeasure,
      song,
      seams: item.seams,
    });

    nextIndexBySection.set(sectionId, indexInSection + units.length);
    phrases.push(...units);
  }

  return phrases;
}

/**
 * Section owning a phrase, resolved on its `startTick`.
 *
 * The analyzer forces section boundaries to also be phrase boundaries,
 * so a phrase normally sits inside exactly one section. When one does
 * straddle two, we attach it to the section it *starts* in: that is
 * where the player meets it, and it keeps `indexInSection` monotonic
 * along the journey. Such a phrase then legitimately runs past its
 * section's `endMeasure`, we do not clip it.
 *
 * No section covers the start tick (gap between sections, sections
 * missing altogether) → sentinel, so the phrase still gets an id.
 */
function resolveSectionId(
  phrase: AnalysisPhrase,
  sections: readonly AnalysisSection[],
): string {
  for (const section of sections) {
    if (
      phrase.startTick >= section.startTick &&
      phrase.startTick < section.endTick
    ) {
      return section.id;
    }
  }

  return FALLBACK_SECTION_ID;
}

// ── Fallbacks (degenerate analyses) ───────────────────────────────

/**
 * Fallback: 1 phrase per section, boundaries taken as-is.
 *
 * Reached when the analysis carries no usable phrase (older payload,
 * generated exercise, silent piece). Sections are coarser than phrases
 * and can span 16+ measures, so this is a downgraded experience, not a
 * substitute algorithm.
 */
function buildSectionPhrases(
  sections: readonly AnalysisSection[],
  song: SongLike,
): Phrase[] {
  // One analyzer span per section, which the dosage may still cut: a
  // section is coarser than a phrase, so this fallback is exactly where a
  // span runs long.
  return sections.flatMap((section) =>
    buildUnits({
      sectionId: section.id,
      indexInSection: 0,
      startTick: section.startTick,
      endTick: section.endTick,
      startMeasure: section.startMeasure,
      endMeasure: section.endMeasure,
      song,
    }),
  );
}

/** Fallback of last resort: regular grid over the whole song. */
function buildSongGridPhrases(
  song: SongLike,
  bars: readonly { startTick: number; endTick: number }[],
): Phrase[] {
  const phrases: Phrase[] = [];
  const totalBars = bars.length;
  let cursor = 0;
  let indexInSection = 0;

  while (cursor < totalBars) {
    const chunkSize = Math.min(
      FALLBACK_MEASURES_PER_PHRASE,
      totalBars - cursor,
    );
    const startMeasure = cursor + 1;
    const endMeasure = cursor + chunkSize;
    const startTick = bars[cursor].startTick;
    const endTick = bars[cursor + chunkSize - 1].endTick;

    const units = buildUnits({
      sectionId: FALLBACK_SECTION_ID,
      indexInSection,
      startTick,
      endTick,
      startMeasure,
      endMeasure,
      song,
    });

    phrases.push(...units);

    cursor += chunkSize;
    indexInSection += units.length;
  }

  return phrases;
}

// ── Phrase factory ────────────────────────────────────────────────

type BuildPhraseArgs = {
  sectionId: string;
  indexInSection: number;
  startTick: number;
  endTick: number;
  startMeasure: number;
  endMeasure: number;
  song: SongLike;
  /** Seams the analyzer published inside this span, when it has any. */
  seams?: readonly AnalysisPhraseSeam[];
};

/**
 * One analyzer phrase → the work units the journey practises.
 *
 * The span given here is the musical object; what comes back is one or more
 * `Phrase` values, each a unit. Only the first opens a musical phrase, and
 * `indexInSection` keeps running across units so ids stay unique and
 * monotonic along the journey.
 */
function buildUnits(args: BuildPhraseArgs): Phrase[] {
  const whole = buildPhrase(args, true);
  const units = buildWorkUnits(whole, candidatesFor(args));

  if (units.length <= 1) {
    return [whole];
  }

  return units.map((unit, i) =>
    buildPhrase(
      {
        ...args,
        indexInSection: args.indexInSection + i,
        startTick: unit.startTick,
        endTick: unit.endTick,
        startMeasure: unit.startMeasure,
        endMeasure: unit.endMeasure,
      },
      i === 0,
    ),
  );
}

function buildPhrase(
  args: BuildPhraseArgs,
  opensMusicalPhrase: boolean,
): Phrase {
  const { left, right } = collectNotesByHand(
    args.song,
    args.startTick,
    args.endTick,
  );

  return {
    id: `${args.sectionId}:${args.indexInSection}` as PhraseId,
    sectionId: args.sectionId,
    indexInSection: args.indexInSection,
    startTick: args.startTick,
    endTick: args.endTick,
    startMeasure: args.startMeasure,
    endMeasure: args.endMeasure,
    notesByHand: { left, right },
    gripsByHand: {
      left: clipGrips(
        args.song,
        'left',
        args.startTick,
        args.endTick,
        args.startMeasure,
        args.endMeasure,
        left,
      ),
      right: clipGrips(
        args.song,
        'right',
        args.startTick,
        args.endTick,
        args.startMeasure,
        args.endMeasure,
        right,
      ),
    },
    opensMusicalPhrase,
  };
}

/**
 * The seams the analyzer published inside this span, as candidates.
 *
 * Empty on an analysis produced before the analyzer emitted them, and then
 * the phrase stays whole. That is a repli, never a licence to score bar
 * lines here: the phrase detector already scores every one of them, and a
 * copy made on this side would be a weaker second opinion.
 */
function candidatesFor(args: BuildPhraseArgs): CutCandidate[] {
  const chordStarts = new Set(args.song.getChordStartTicks());

  return (args.seams ?? []).map((seam) =>
    toCutCandidate(seam, chordStarts.has(seam.tick)),
  );
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * Analyzer grips of one hand, cut to `[startTick, endTick)`.
 *
 * The keys and the fingering are kept as the analyzer gave them: a grip is
 * by definition a stretch during which no finger changes key, so its
 * assignment is true over any part of it, including a part cut off by a unit
 * boundary. `attacks`, on the other hand, is recounted here — the analyzer
 * counts onsets over the whole grip, and telling the player "seven notes are
 * played from here" while three of them fall in the next unit would be false
 * where it is read.
 *
 * Onsets are counted, not notes: a three-note chord is one attack, the same
 * convention the analyzer uses.
 */
function clipGrips(
  song: SongLike,
  hand: 'left' | 'right',
  startTick: number,
  endTick: number,
  startMeasure: number,
  endMeasure: number,
  notes: readonly PhraseNote[],
): PhraseGrip[] {
  const all = song.getHandGrips();

  if (all === null) {
    return [];
  }

  // Counted over the piece, not the unit: what makes a configuration worth
  // learning is how often it comes back overall.
  const shapeCounts = new Map<string, number>();

  for (const grip of all[hand]) {
    shapeCounts.set(grip.shape, (shapeCounts.get(grip.shape) ?? 0) + 1);
  }

  const out: PhraseGrip[] = [];

  for (const grip of all[hand]) {
    const from = Math.max(grip.startTick, startTick);
    const to = Math.min(grip.endTick, endTick);

    if (to <= from) {
      continue;
    }

    const midis = new Set(grip.fingers.map((f) => f.midi));
    const onsets = new Set<number>();

    for (const note of notes) {
      // Restricted to the grip's own keys, not merely to its tick span. A
      // note the analyzer could not finger is inside the span and outside
      // the key set — it is dropped upstream rather than closing the grip —
      // so counting it would make `attacks` mean "onsets while this grip was
      // open" where the field says "onsets played *from here*".
      //
      // It is also the only realistic route to `openingKeys`' fallback:
      // an `attacks > 0` bought by a note outside the key set leaves that
      // function with nothing to find, and the panel silently collapses to
      // one act — "prends A, B et C ensemble" — over a broken figure.
      if (note.startTick >= from && note.startTick < to && midis.has(note.midi)) {
        onsets.add(note.startTick);
      }
    }

    out.push({
      holdable: grip.holdable,
      // Derived, not transported: the payload states the assignment, and the
      // key set is its image. Two fingers can share a key, so it is the
      // deduplicated image.
      midis: [...midis].sort((a, b) => a - b),
      fingers: grip.fingers.map(({ finger, midi }) => ({ finger, midi })),
      shape: grip.shape,
      startTick: from,
      endTick: to,
      startMeasure: clamp(grip.startMeasure, startMeasure, endMeasure),
      endMeasure: clamp(grip.endMeasure, startMeasure, endMeasure),
      attacks: onsets.size,
      shapeCountInPiece: shapeCounts.get(grip.shape) ?? 1,
    });
  }

  return out;
}

/**
 * Collect notes that fall in `[startTick, endTick)` for each hand.
 *
 * Mirrors `lib/music/slice-computation.ts#collectNotesByHand` but on
 * the `PartLike` / `AnnotatedNoteLike` structural surface, and reduced
 * to the `PhraseNote` shape (no harmony, no fingering, no section id).
 *
 * Notes with `hand === null` are dropped: the journey FSM only routes
 * left/right.
 */
function collectNotesByHand(
  song: SongLike,
  startTick: number,
  endTick: number,
): { left: PhraseNote[]; right: PhraseNote[] } {
  const left: PhraseNote[] = [];
  const right: PhraseNote[] = [];

  for (const part of song.getParts()) {
    for (const note of part.getAnnotatedNotes()) {
      if (note.tick < startTick || note.tick >= endTick) {
        continue;
      }

      const phraseNote: PhraseNote = {
        midi: note.note.midi,
        startTick: note.tick,
        endTick: note.tick + note.durationTicks,
        spelling: note.spelling ?? null,
      };

      if (note.hand === 'left') {
        left.push(phraseNote);
      } else if (note.hand === 'right') {
        right.push(phraseNote);
      }
    }
  }

  return { left, right };
}

// ── Hash (FNV-1a 32-bit) ──────────────────────────────────────────

/**
 * FNV-1a 32-bit hash, returned as 8-char lowercase hex.
 *
 * Reference: http://www.isthe.com/chongo/tech/comp/fnv/
 *   hash = OFFSET_BASIS
 *   for each byte b:
 *     hash = (hash XOR b) * FNV_PRIME  (mod 2^32)
 *
 * `Math.imul` keeps the multiply within 32-bit two's complement,
 * `>>> 0` reinterprets as unsigned for the final hex emit.
 */
function fnv1a32Hex(input: string): string {
  const FNV_OFFSET_BASIS = 0x811c9dc5;
  const FNV_PRIME = 0x01000193;
  let hash = FNV_OFFSET_BASIS;

  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME);
  }

  return (hash >>> 0).toString(16).padStart(8, '0');
}
